import { Layout } from "@jambushrushti/components/layout/Layout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Link } from "react-router-dom";
import { useCMSContent } from "@jambushrushti/hooks/useCMSContent";
import {
  ArrowRight,
  BookOpen,
  Users,
  Building2,
  Lightbulb,
  Target,
  Heart,
  Scroll,
} from "lucide-react";

const visionItems = [
  {
    icon: Lightbulb,
    title: "Preserve",
    description:
      "Safeguard ancient Jain texts, manuscripts, and teachings for future generations",
  },
  {
    icon: Target,
    title: "Research",
    description:
      "Enable scholars worldwide to explore, study, and contribute to Jain knowledge",
  },
  {
    icon: Heart,
    title: "Connect",
    description:
      "Build a global community united by the pursuit of spiritual wisdom",
  },
];

const sections = [
  {
    icon: Scroll,
    title: "Gurudev Muni Jambuvijayaji Maharaj Saheb",
    description:
      "Explore the life, teachings, and legacy of the revered Jain saint and scholar",
    href: "/about/gurudev",
  },
  {
    icon: Users,
    title: "Legacy of Pujyapad Muni Jambuvijayaji Maharaj",
    description: "Learn about the lineage, disciples, and their contributions",
    href: "/about/parivar",
  },
  {
    icon: Building2,
    title: "Muni Jambuvijayaji Research Center",
    description:
      "Visit our digital museum preserving Gurudev's creations and works",
    href: "/about/gyan-kendra",
  },
];

const About = () => {
  const cmsContent = useCMSContent();

  return (
    <Layout>
      <Seo
        title="About"
        description="Learn about Jambushrusti - a comprehensive digital ecosystem dedicated to preserving and sharing the teachings of Gurudev Muni Jambuvijayaji Maharaj Saheb."
      />
      {/* Hero */}
      <section className="bg-gradient-hero lotus-pattern py-5 sm:py-8 lg:py-14">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="font-heading animate-fade-up mb-3 text-3xl font-bold text-foreground sm:mb-6 sm:text-5xl">
              {cmsContent.aboutTitle}
            </h1>
            <p className="font-body animate-fade-up text-sm text-muted-foreground delay-100 sm:text-base">
              {cmsContent.aboutDescription}
            </p>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="bg-background py-6 sm:py-10 lg:py-14">
        <div className="container mx-auto px-4">
          <div className="mb-6 text-center sm:mb-8">
            <h2 className="font-heading mb-3 text-2xl font-bold text-foreground sm:mb-4 sm:text-3xl">
              Our Vision & Mission
            </h2>
            <p className="font-body mx-auto max-w-2xl text-sm text-muted-foreground sm:text-base">
              To create the world's most comprehensive and accessible platform
              for Jain scholarship and spiritual exploration.
            </p>
          </div>

          <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-3">
            {visionItems.map((item, index) => (
              <div
                key={item.title}
                className="animate-fade-up text-center"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                  <item.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-heading mb-2 text-xl font-semibold text-foreground">
                  {item.title}
                </h3>
                <p className="font-body text-muted-foreground">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="bg-gradient-spiritual py-6 sm:py-10 lg:py-14">
        <div className="container mx-auto px-4">
          <div className="mb-6 text-center sm:mb-8">
            <h2 className="font-heading mb-3 text-2xl font-bold text-foreground sm:mb-4 sm:text-3xl">
              What Jambushrusti Offers
            </h2>
          </div>

          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: "Guruvani Collection",
                desc: "Sacred discourses and teachings",
              },
              {
                title: "Research Ecosystem",
                desc: "Four integrated research tools",
              },
              {
                title: "Digital Library",
                desc: "Extensive catalog of Jain literature",
              },
              { title: "Scholar Portal", desc: "Community for researchers" },
              {
                title: "AI-Powered Search",
                desc: "Search inside PDFs and books",
              },
              {
                title: "Events & Workshops",
                desc: "Regular lectures and gatherings",
              },
            ].map((item, index) => (
              <Card
                key={item.title}
                variant="feature"
                className="animate-fade-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6">
                  <BookOpen className="mb-3 h-6 w-6 text-primary" />
                  <h3 className="font-heading mb-1 font-semibold text-foreground">
                    {item.title}
                  </h3>
                  <p className="font-body text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Explore Sections */}
      <section className="bg-background py-6 sm:py-10 lg:py-14">
        <div className="container mx-auto px-4">
          <div className="mb-6 text-center sm:mb-8">
            <h2 className="font-heading mb-4 text-3xl font-bold text-foreground">
              Explore More
            </h2>
          </div>

          <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-3">
            {sections.map((section, index) => (
              <Card
                key={section.title}
                variant="interactive"
                className="animate-fade-up group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardHeader>
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 transition-colors group-hover:bg-primary/20">
                    <section.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="font-heading text-lg">
                    {section.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-body mb-4 text-muted-foreground">
                    {section.description}
                  </p>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={section.href}>
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
